/* eslint-disable max-classes-per-file */
import * as R from 'ramda'
import { CallType } from '@weblite/ts-action/media-proxy'
import { generateWappSessionId } from 'weblite-web-wapprunner'
import {
  eventCallMutedChanged,
  eventHistoryChanged,
  eventLeftCall,
  eventWappVisibilityChanged,
  eventCallParticipantsCountReady,
  HistoryEvent,
  Meta,
  WappInput,
  WappMode,
} from '@weblite-wapps/protocol'
import {
  getPanelHistory,
  getPanelTop,
  getSectionsHistory,
  getSectionsHistoryTop,
  getSectionTop,
  HistoryItemState,
  listen,
  PanelHistoryItem,
  PanelHistoryItemWithDelimiter,
  popFromPanel,
  pushToPanel,
  replaceInPanel,
  resetPanel,
} from 'weblite-router'
import { WappPanelState } from './types'
import { callAPI } from '$scenes/call'
import { vitrineAPI } from '$scenes/vitrine'
import { courseVitrineAPI } from '$scenes/course-vitrine'
import {
  hideCall,
  hideCourseVitrine,
  hideVitrine,
  openCall as openCallPanel,
  openCourseVitrine,
  openVitrine,
  switchToJaryan,
  switchToKooleh,
  switchToMavakeb,
  switchToRahkar,
  switchToRoomList,
} from '$logic/router'
import { getOpenedRoomId } from '$logic/opened-room'
import { getRoomPermissions, isClientsRoom } from '$logic/room'
import {
  StaticPanelType,
  StaticPanelWappId,
} from '$logic/wapp-panels/static-wapp-panels.utils'
import { sendEventToWapp } from '$logic/wapp-communication/wapp-communication.publish'
import { callStorage } from '$logic/call'
import { asyncDelay } from '$func/delay'
import { isDesktopSize } from '$func/environmentInfo.js'

class StaticWappPanel {
  protected static panel: StaticPanelType
  protected static wappMeta: Meta

  static getName() {
    return this.panel
  }

  static getPanelHistory() {
    return getPanelHistory(this.panel)
  }

  static pushToPanel(data: WappPanelState, dontUpdateSections = false) {
    return pushToPanel(this.panel, this.panel, data, dontUpdateSections)
  }

  static replaceInPanel(data: WappPanelState) {
    return replaceInPanel(this.panel, this.panel, data)
  }

  static popFromPanel() {
    return popFromPanel(this.panel)
  }

  static resetPanel(...initialData: HistoryItemState[]) {
    return resetPanel(this.panel, ...initialData)
  }

  // Use this function to mount WapprunnerCreate component
  static getWappMeta() {
    return this.wappMeta
  }

  static getWappHistory() {
    return (
      this.getPanelHistory()
        // @ts-ignore
        .filter(state => state !== 'INIT')
        .map(item => (item as PanelHistoryItem).state)
    )
  }

  static async close() {
    await switchToRoomList()
  }

  static isStaticPanelVisible() {
    const leftSectionTop = getSectionTop('left')
    const homePanelState = getPanelTop('home')?.state
    const homeActiveTab = homePanelState?.tab

    const leftSectionIsVisible = isDesktopSize()
      ? getSectionsHistory().reduce(
          (acc, section) =>
            ['left', 'channelVitrine', 'courseVitrine'].includes(section)
              ? section
              : acc,
          '',
        ) === 'left'
      : getSectionsHistoryTop() === 'left'

    return (
      leftSectionIsVisible &&
      leftSectionTop === 'home' &&
      homeActiveTab === this.panel
    )
  }

  static listenToHistoryEvents() {
    let lastWappHistory: PanelHistoryItemWithDelimiter[]
    let lastIsVisible: boolean

    listen(event => {
      if (!this.wappMeta?.wappSessionId) return

      const isVisible = this.isStaticPanelVisible()

      if (lastIsVisible && !isVisible)
        sendEventToWapp(
          eventWappVisibilityChanged({ visible: false }, this.wappMeta),
        )

      if (!lastIsVisible && isVisible)
        sendEventToWapp(
          eventWappVisibilityChanged({ visible: true }, this.wappMeta),
        )

      lastIsVisible = isVisible
      const wappHistory = this.getWappHistory()
      if (R.equals(wappHistory, lastWappHistory)) return

      lastWappHistory = wappHistory.slice()
      sendEventToWapp(
        eventHistoryChanged(
          {
            type: event as HistoryEvent,
            newHistory: wappHistory.filter(Boolean),
          },
          this.wappMeta,
        ),
      )
    })
  }
}

export class KoolehWappPanel extends StaticWappPanel {
  protected static panel: StaticPanelType = 'kooleh'

  static mount() {
    this.wappMeta = {
      wappId: StaticPanelWappId.KOOLEH,
      wappSessionId: generateWappSessionId(),
      mode: WappMode.STATIC,
    }

    this.pushToPanel({ data: 'BASE_STATE' }, true)
  }

  static open() {
    switchToKooleh()
  }
}

export class JaryanWappPanel extends StaticWappPanel {
  protected static panel: StaticPanelType = 'jaryan'

  static mount() {
    this.wappMeta = {
      wappId: StaticPanelWappId.JARYAN,
      wappSessionId: generateWappSessionId(),
      mode: WappMode.STATIC,
    }

    this.pushToPanel({ data: 'BASE_STATE' }, true)
  }

  static open() {
    switchToJaryan()
  }
}

export class VitrineWappPanel extends StaticWappPanel {
  protected static panel: StaticPanelType = 'vitrine'
  protected static isMookeb: boolean

  static popFromPanel() {
    const panelHistory = this.getPanelHistory()

    const [item] = panelHistory.slice(-2, -1)
    if (!item || item === 'INIT') {
      hideVitrine()
    } else popFromPanel(this.panel)
  }

  static mount() {
    this.wappMeta = {
      wappId: StaticPanelWappId.VITRINE,
      wappSessionId: generateWappSessionId(),
      mode: WappMode.STATIC,
    }
  }

  static async open(input: { roomId?: string; isMookeb?: boolean } = {}) {
    const { roomId, isMookeb = false } = input

    const channelId = roomId || getOpenedRoomId()
    const permissions = await getRoomPermissions(channelId)

    if (this.isMookeb !== isMookeb) {
      this.wappMeta = {
        wappId: isMookeb
          ? StaticPanelWappId.MOOKEB_VITRINE
          : StaticPanelWappId.VITRINE,
        wappSessionId: generateWappSessionId(),
        mode: WappMode.STATIC,
      }

      vitrineAPI.setWappMeta(this.wappMeta)
      this.isMookeb = isMookeb
    }

    vitrineAPI.setInput({
      channelId,
      hasEditAccess: permissions?.editMessage,
      isSubscriber: isClientsRoom(channelId),
    })

    this.resetPanel({
      location: 'vitrine',
      state: { data: { type: 'page', location: 'root' } },
    })

    openVitrine()
  }

  static async close() {
    await hideVitrine()
  }
}

export class CourseVitrineWappPanel extends StaticWappPanel {
  protected static panel: StaticPanelType = 'courseVitrine'

  static popFromPanel() {
    const panelHistory = this.getPanelHistory()

    const [item] = panelHistory.slice(-2, -1)
    if (!item || item === 'INIT') {
      hideVitrine()
    } else popFromPanel(this.panel)
  }

  static mount() {
    this.wappMeta = {
      wappId: StaticPanelWappId.COURSE_VITRINE,
      wappSessionId: generateWappSessionId(),
      mode: WappMode.STATIC,
    }
  }

  static async open(input: { roomId?: string; isAdmin: boolean } = {}) {
    const { roomId, isAdmin = false } = input
    const channelId = roomId || getOpenedRoomId()

    this.wappMeta = {
      wappId: StaticPanelWappId.COURSE_VITRINE,
      wappSessionId: this.wappMeta?.wappSessionId ?? generateWappSessionId(),
      mode: WappMode.STATIC,
    }

    courseVitrineAPI.setWappMeta(this.wappMeta)
    courseVitrineAPI.setInput({
      channelId,
      isAdmin,
    })

    this.resetPanel({
      location: 'courseVitrine',
      state: { data: { type: 'page', location: 'root' } },
    })

    openCourseVitrine()
  }

  static async close() {
    await hideCourseVitrine()
  }
}

export class RahkarWappPanel extends StaticWappPanel {
  protected static panel: StaticPanelType = 'rahkar'

  static mount() {
    this.wappMeta = {
      wappId: StaticPanelWappId.RAHKAR,
      wappSessionId: generateWappSessionId(),
      mode: WappMode.STATIC,
    }

    this.pushToPanel({ data: 'BASE_STATE' })
  }

  static open() {
    switchToRahkar()
  }

  static async close() {
    popFromPanel('home')
  }
}

export class MavakebWappPanel extends StaticWappPanel {
  protected static panel: StaticPanelType = 'mavakeb'

  static mount() {
    this.wappMeta = {
      wappId: StaticPanelWappId.MAVAKEB,
      wappSessionId: generateWappSessionId(),
      mode: WappMode.STATIC,
    }

    this.pushToPanel({ data: 'BASE_STATE' }, true)
  }

  static open() {
    switchToMavakeb()
  }
}

const callTypeToWappId: Record<CallType, string> = {
  call: 'call-call',
  group_call: 'call-meet',
  tribune: 'call-tribune',
  live: 'call-live',
  classroom: 'call-classroom',
}
export class CallWappPanel extends StaticWappPanel {
  protected static panel: StaticPanelType = 'call'

  protected static currentToken: string = ''

  static openCall(callType: CallType, token: string, input?: WappInput) {
    const wappSessionId =
      this.currentToken !== token
        ? generateWappSessionId()
        : this.wappMeta.wappSessionId

    if (this.currentToken !== token) {
      this.resetPanel({
        location: 'call',
        state: { data: { type: 'page', location: 'root' } },
      })

      this.currentToken = token
    }

    this.wappMeta = {
      wappId: callTypeToWappId[callType],
      wappSessionId,
      mode: WappMode.STATIC,
    }

    callAPI.refreshCall(this.currentToken, input)
    openCallPanel()
  }

  static async close() {
    hideCall()
  }

  static setMuted(muted: boolean) {
    callStorage.set({ muted })
    sendEventToWapp(eventCallMutedChanged({ muted }, this.wappMeta))
  }

  static leaveCall() {
    sendEventToWapp(eventLeftCall(null, this.wappMeta))
  }

  static sendParticipantsCountToWapp(participantsCount: number) {
    sendEventToWapp(
      eventCallParticipantsCountReady({ participantsCount }, this.wappMeta),
    )
  }

  static async closeCall(hideCallPanel = true, sendEndCallEventToWapp = true) {
    this.currentToken = ''
    if (hideCallPanel) hideCall()

    if (this.wappMeta) {
      if (sendEndCallEventToWapp) this.leaveCall()

      // TODO[router]: Refactor with onRouterTransitionEnd
      if (!isDesktopSize()) await asyncDelay(200)

      this.wappMeta.wappSessionId = ''
    }

    callAPI.refreshCall('', {})
  }
}
